const DAY_MS = 24 * 60 * 60 * 1000;

export const TRIAL_BANNER_DISMISS_MS = DAY_MS;
export const ENDING_SOON_MAX_DAYS = 3;

// Set to a date (e.g. "2026-10-23T18:00:00Z") to preview trial UI locally.
// Only honoured in dev builds.
const MOCK_TRIAL_ENDS_AT = null;

export const TRIAL_STAGES = {
  NONE: "none",
  ACTIVE: "active",
  ENDING_SOON: "endingSoon",
  LAST_DAY: "lastDay",
  EXPIRED: "expired",
};

export const parseTrialDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  let date;
  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const numeric = Number(value);
    // Backend sends Unix seconds in some payloads and milliseconds in others.
    date = new Date(numeric > 9999999999 ? numeric : numeric * 1000);
  } else {
    date = new Date(value);
  }

  return Number.isNaN(date.getTime()) ? null : date;
};

const readUpper = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();


export const getTrialEndsAt = (entitlements, { mock = MOCK_TRIAL_ENDS_AT } = {}) => {
  if (mock && import.meta.env.DEV) return parseTrialDate(mock);

  const raw = entitlements?.raw || {};
  const subscription = raw?.subscription || {};
  const isTrial =
    [raw?.source, entitlements?.source, subscription?.source]
      .map(readUpper)
      .includes("TRIAL") ||
    [raw?.status, subscription?.status].map(readUpper).includes("TRIALING");

  if (!isTrial) return null;

  const period = entitlements?.period || raw?.period || {};
  return (
    [
      raw?.trialEndsAt,
      raw?.trialEnd,
      subscription?.trialEnd,
      subscription?.trial_end,
      period?.end,
    ]
      .map(parseTrialDate)
      .find(Boolean) || null
  );
};

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Calendar days between today and the end date (0 = ends today). */
export const getTrialDaysLeft = (trialEndsAt, now = new Date()) => {
  const end = parseTrialDate(trialEndsAt);
  if (!end) return null;
  // Math.round absorbs the ±1h shift on DST changeover days.
  return Math.round((startOfLocalDay(end) - startOfLocalDay(now)) / DAY_MS);
};

export const getTrialStage = (trialEndsAt, now = new Date()) => {
  const end = parseTrialDate(trialEndsAt);
  if (!end) return TRIAL_STAGES.NONE;
  if (end.getTime() <= now.getTime()) return TRIAL_STAGES.EXPIRED;

  const daysLeft = getTrialDaysLeft(end, now);
  if (daysLeft <= 0) return TRIAL_STAGES.LAST_DAY;
  if (daysLeft <= ENDING_SOON_MAX_DAYS) return TRIAL_STAGES.ENDING_SOON;
  return TRIAL_STAGES.ACTIVE;
};

const pluralDays = (count) => `${count} ${count === 1 ? "day" : "days"}`;

export const getTrialChip = (stage, daysLeft) => {
  switch (stage) {
    case TRIAL_STAGES.ACTIVE:
      return { tone: "info", label: `Trial · ${pluralDays(daysLeft)} left` };
    case TRIAL_STAGES.ENDING_SOON:
      return { tone: "warning", label: `Trial · ${pluralDays(daysLeft)} left` };
    // No chip on the last day: the countdown banner takes over.
    case TRIAL_STAGES.EXPIRED:
      return { tone: "danger", label: "Trial ended" };
    default:
      return null;
  }
};

export const TRIAL_BANNER_ID = "trial-status";

export const TRIAL_AUTO_RENEW_NOTE =
  "Your subscription will start automatically after that.";

/**
 * Banner message for the BannerManager, or null when the stage has none.
 * Trials convert to the paid plan automatically, so these are informational
 * only: no call to action, and nothing to show once the trial has ended.
 * On the last day the message carries `countdownTo` so the UI can render a
 * live countdown in place of the static `text`.
 */
export const getTrialBannerMessage = (stage, daysLeft, { trialEndsAt = null } = {}) => {
  switch (stage) {
    case TRIAL_STAGES.ENDING_SOON:
      return {
        id: TRIAL_BANNER_ID,
        priority: 100,
        type: "warning",
        text: `Your trial ends in ${pluralDays(daysLeft)}. ${TRIAL_AUTO_RENEW_NOTE}`,
        dismissible: true,
      };
    case TRIAL_STAGES.LAST_DAY:
      return {
        id: TRIAL_BANNER_ID,
        priority: 100,
        type: "warning",
        text: "Your trial ends today.",
        countdownTo: parseTrialDate(trialEndsAt),
        dismissible: false,
      };
    default:
      return null;
  }
};

/** Milliseconds -> "4h 12m", "12m", or "less than a minute". Never negative. */
export const formatTrialCountdown = (remainingMs) => {
  const totalMinutes = Math.max(0, Math.floor(remainingMs / 60000));
  if (totalMinutes < 1) return "less than a minute";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

// Dismissal only applies to the ending-soon stage; the final day and expiry
// always re-show regardless of what is stored.
const isDismissibleStage = (stage) => stage === TRIAL_STAGES.ENDING_SOON;

const dismissKey = (userKey) => `trialBannerDismissedUntil:${userKey}`;
const modalKey = (userKey) => `trialEndsTodayModalShownOn:${userKey}`;

const localDateStamp = (now) =>
  `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;

export const isTrialBannerDismissed = (userKey, stage, now = new Date()) => {
  if (!userKey || !isDismissibleStage(stage)) return false;
  try {
    const until = Number(localStorage.getItem(dismissKey(userKey)));
    return Number.isFinite(until) && until > now.getTime();
  } catch (error) {
    console.warn("Could not read trial banner preference", error);
    return false;
  }
};

export const dismissTrialBanner = (userKey, now = new Date()) => {
  if (!userKey) return;
  try {
    localStorage.setItem(
      dismissKey(userKey),
      String(now.getTime() + TRIAL_BANNER_DISMISS_MS),
    );
  } catch (error) {
    console.warn("Could not save trial banner preference", error);
  }
};

export const shouldShowTrialEndsTodayModal = (userKey, stage, now = new Date()) => {
  if (!userKey || stage !== TRIAL_STAGES.LAST_DAY) return false;
  try {
    return localStorage.getItem(modalKey(userKey)) !== localDateStamp(now);
  } catch (error) {
    console.warn("Could not read trial modal preference", error);
    // Without storage we can't guarantee "once per day", so stay quiet.
    return false;
  }
};

export const markTrialEndsTodayModalShown = (userKey, now = new Date()) => {
  if (!userKey) return;
  try {
    localStorage.setItem(modalKey(userKey), localDateStamp(now));
  } catch (error) {
    console.warn("Could not save trial modal preference", error);
  }
};
